import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Check, ExternalLink, Loader2, LockKeyhole, Search, ShieldCheck, Wrench } from "lucide-react";
import { toast } from "sonner";
import { trackAdminWorkflowCompleted } from "../utils/adminPortalRollout";

import { API, useAuth } from "../App";
import AdminLayout from "../components/AdminLayout";
import EditorialPageHeader from "../components/EditorialPageHeader";
import "./athleticTools.css";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { marketplaceAppIsInstalled } from "../utils/marketplaceInstallations";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../components/ui/alert-dialog";


const statusLabel = {
  available: "Available",
  coming_soon: "Coming soon",
  managed: "Connected integration",
};

const marketplaceLogos = {
  squadline: "https://customer-assets.emergentagent.com/job_7d56551f-fdbd-484f-b373-af315d480d92/artifacts/ife1p25x_Squadline%20%28400%20x%20100%20px%29.png",
  wstl: "https://wstlofficiating.com/wstl-logo-approved.png",
};

const marketplaceLogo = (module) => marketplaceLogos[module.module_id] || "/brand/sporthouse-footer-logo.png";

const billingDate = (value) => {
  if (!value) return null;
  const date = new Date(typeof value === "number" ? value * 1000 : value);
  return Number.isNaN(date.getTime()) ? null : date.toLocaleDateString();
};

const EmbeddedLayout = ({ children }) => <>{children}</>;

const legacyModuleDestinations = {
  "/admin/wstl": "wstl",
  "/admin/modules/tournaments": "tournaments",
  "/admin/modules/leagues": "leagues",
  "/admin/developer": "developer",
};

const AdminMarketplace = ({ embedded = false, routeBase = "/admin" }) => {
  const PageLayout = embedded ? EmbeddedLayout : AdminLayout;
  const { token } = useAuth();
  const navigate = useNavigate();
  const [modules, setModules] = useState([]);
  const [view, setView] = useState("discover");
  const [query, setQuery] = useState("");
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [changing, setChanging] = useState(null);
  const [pendingRemoval, setPendingRemoval] = useState(null);
  const moduleDestination = (launchPath) => {
    const destination = legacyModuleDestinations[launchPath];
    return embedded && destination ? `${routeBase}/${destination}` : launchPath;
  };

  const loadCatalog = useCallback(async () => {
    setLoading(true);
    setLoadError(false);
    try {
      const response = await axios.get(`${API}/marketplace/apps`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setModules((response.data.apps || []).map((module) => ({
        ...module,
        module_id: module.slug,
        summary: module.description,
        status: module.availability,
        pricing: module.pricing_model,
        scopes: module.permissions,
        installed: marketplaceAppIsInstalled(module),
        billing: module.billing || null,
      })));
    } catch (error) {
      console.error("Failed to load Marketplace", error);
      setLoadError(true);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    loadCatalog();
  }, [loadCatalog]);

  const filteredModules = useMemo(() => {
    const normalized = query.trim().toLowerCase();
    return modules.filter((module) => {
      if (view === "installed" && !module.installed) return false;
      if (!normalized) return true;
      return [module.name, module.summary, module.category, module.publisher]
        .some((value) => value?.toLowerCase().includes(normalized));
    });
  }, [modules, query, view]);

  const handleViewKeyDown = (event, currentView) => {
    const views = ["discover", "installed"];
    const currentIndex = views.indexOf(currentView);
    let nextIndex;
    if (event.key === "ArrowRight") nextIndex = (currentIndex + 1) % views.length;
    else if (event.key === "ArrowLeft") nextIndex = (currentIndex - 1 + views.length) % views.length;
    else if (event.key === "Home") nextIndex = 0;
    else if (event.key === "End") nextIndex = views.length - 1;
    else return;

    event.preventDefault();
    const nextView = views[nextIndex];
    setView(nextView);
    window.requestAnimationFrame(() => document.getElementById(`marketplace-${nextView}-tab`)?.focus());
  };

  const changeInstallation = async (module) => {
    setChanging(module.module_id);
    try {
      const url = `${API}/marketplace/apps/${module.module_id}/install`;
      const config = { headers: { Authorization: `Bearer ${token}` } };
      if (module.installed) {
        await axios.delete(url, config);
        toast.success(`${module.name} removed`);
      } else {
        const response = await axios.post(url, { action: "install" }, config);
        if (response.data.checkout_url) {
          window.location.assign(response.data.checkout_url);
          return;
        }
        toast.success(`${module.name} installed`);
      }
      await loadCatalog();
      trackAdminWorkflowCompleted("marketplace");
      window.dispatchEvent(new CustomEvent("marketplace-installations-changed"));
      setPendingRemoval(null);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Could not update this module");
    } finally {
      setChanging(null);
    }
  };

  const manageBilling = async (module) => {
    setChanging(module.module_id);
    try {
      const billingPath = module.module_id === "website_pro" ? "website/billing" : "officiating/billing";
      const response = await axios.post(`${API}/${billingPath}/${module.installation.org_id}/portal`, {}, {
        headers: { Authorization: `Bearer ${token}` },
      });
      window.location.assign(response.data.portal_url);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Could not open billing management");
      setChanging(null);
    }
  };

  return (
    <PageLayout
      title="Marketplace"
      subtitle="Build the operating system your organization needs with capabilities approved for your team."
      headerEyebrow="Extend Sporthouse"
    >
      <div className="athletic-tools space-y-6" data-testid="marketplace-workspace">
        {embedded && <EditorialPageHeader
          className="athletic-tools-heading"
          headingId="marketplace-heading"
          title="Marketplace"
          description="Build the operating system your organization needs with capabilities approved for your team."
        />}
        <section className="overflow-hidden rounded-[28px] border border-[#dfe6df] bg-[#fbfcf8] shadow-[0_18px_50px_rgba(16,42,39,0.08)]">
          <div className="flex flex-col gap-4 border-b border-[#dfe6df] px-5 py-4 sm:flex-row sm:items-center sm:justify-between lg:px-8">
            <div className="inline-flex rounded-2xl bg-[#edf2ed] p-1" role="tablist" aria-label="Marketplace views">
              {[["discover", "Discover"], ["installed", `Installed (${modules.filter((item) => item.installed).length})`]].map(([key, label]) => (
                <button key={key} id={`marketplace-${key}-tab`} type="button" role="tab" aria-selected={view === key} aria-controls="marketplace-results" tabIndex={view === key ? 0 : -1} onClick={() => setView(key)} onKeyDown={(event) => handleViewKeyDown(event, key)} className={`rounded-xl px-4 py-2 text-sm font-semibold transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#087f67] focus-visible:ring-offset-2 ${view === key ? "bg-white text-[#102a27] shadow-sm" : "text-[#60706a] hover:text-[#102a27]"}`}>
                  {label}
                </button>
              ))}
            </div>
            <div className="flex w-full flex-col gap-3 sm:max-w-xl sm:flex-row sm:items-center sm:justify-end">
              <span className="inline-flex items-center gap-2 text-xs font-semibold text-[#52615b]"><ShieldCheck className="h-4 w-4 text-[#087f67]" /> Admin approved</span>
              <div className="relative w-full sm:max-w-sm">
                <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-[#738079]" />
                <label htmlFor="marketplace-search" className="sr-only">Search Marketplace modules</label>
                <Input id="marketplace-search" type="search" value={query} onChange={(event) => setQuery(event.target.value)} placeholder="Search modules" className="h-11 rounded-2xl border-[#d8e0d8] bg-white pl-10" />
              </div>
            </div>
          </div>

          <div id="marketplace-results" role="tabpanel" aria-labelledby={`marketplace-${view}-tab`} tabIndex={0} className="p-5 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-[#087f67] lg:p-8">
            {loading ? (
              <div className="flex min-h-56 items-center justify-center text-[#60706a]"><Loader2 className="mr-2 h-5 w-5 animate-spin" /> Loading Marketplace</div>
            ) : loadError ? (
              <div className="rounded-2xl border border-[#e5b9ae] bg-[#fff5f1] p-6 text-center">
                <p className="font-semibold text-[#7a2d20]">Marketplace could not be loaded.</p>
                <Button onClick={loadCatalog} className="mt-4 rounded-xl bg-[#102a27]">Try again</Button>
              </div>
            ) : filteredModules.length === 0 ? (
              <div className="rounded-2xl border border-dashed border-[#cad4ca] bg-white p-10 text-center text-[#60706a]">
                {view === "installed" ? "No modules are installed yet." : "No modules match this search."}
              </div>
            ) : (
              <div className="grid gap-5 lg:grid-cols-2 xl:grid-cols-3">
                {filteredModules.map((module) => (
                  <article key={module.module_id} className="flex min-h-[330px] flex-col rounded-[24px] border border-[#dfe6df] bg-white p-5 shadow-[0_10px_30px_rgba(16,42,39,0.05)]">
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex h-14 w-20 items-center justify-center rounded-2xl border border-[#dfe6df] bg-white p-2">
                        <img src={marketplaceLogo(module)} alt={`${module.publisher} logo`} className="max-h-full max-w-full object-contain" />
                      </div>
                      <Badge className={module.status === "managed" ? "bg-[#e9f8f2] text-[#087f67] hover:bg-[#e9f8f2]" : "bg-[#f1f3ef] text-[#60706a] hover:bg-[#f1f3ef]"}>
                        {statusLabel[module.status]}
                      </Badge>
                    </div>
                    <div className="mt-5">
                      <div className="text-xs font-bold uppercase tracking-[0.15em] text-[#087f67]">{module.category}</div>
                      <h2 className="mt-2 text-xl font-semibold text-[#102a27]">{module.name}</h2>
                      <p className="mt-2 text-sm leading-6 text-[#60706a]">{module.summary}</p>
                    </div>
                    <div className="mt-4 flex flex-wrap gap-2">
                      {module.scopes.slice(0, 3).map((scope) => <span key={scope} className="rounded-full bg-[#f1f4ef] px-2.5 py-1 text-xs text-[#52615b]">{scope}</span>)}
                    </div>
                    <div className="mt-auto border-t border-[#edf0ec] pt-5">
                      <div className="mb-4 flex items-center justify-between text-sm"><span className="text-[#718078]">{module.publisher}</span><span className="font-semibold text-[#102a27]">{module.pricing}</span></div>
                      {module.module_id === "wstl" && module.billing && <div className="mb-4 rounded-xl bg-[#f4f7f3] px-3 py-2 text-xs text-[#52615b]">
                        <strong className="block text-[#102a27]">{module.billing.entitlement_type === "complimentary" ? "Complimentary access" : module.billing.status === "grace_period" ? "Payment needs attention" : module.billing.cancel_at_period_end ? "Cancels at period end" : module.billing.status === "active" ? "Subscription active" : "Subscription required"}</strong>
                        {module.billing.status === "grace_period" && billingDate(module.billing.grace_expires_at) && <>Access continues through {billingDate(module.billing.grace_expires_at)}.</>}
                        {module.billing.cancel_at_period_end && billingDate(module.billing.current_period_end) && <>Access continues through {billingDate(module.billing.current_period_end)}.</>}
                        {module.billing.entitlement_type === "complimentary" && billingDate(module.billing.complimentary_expires_at) && <>Access expires {billingDate(module.billing.complimentary_expires_at)}.</>}
                        {module.billing.status === "active" && !module.billing.cancel_at_period_end && module.billing.entitlement_type !== "complimentary" && billingDate(module.billing.current_period_end) && <>Renews {billingDate(module.billing.current_period_end)}.</>}
                      </div>}
                      <Button disabled={module.status !== "available" || changing === module.module_id || (module.installed && !module.launch_path && !(module.module_id === "wstl" && module.billing?.customer_configured))} onClick={() => module.module_id === "wstl" && module.billing?.customer_configured ? manageBilling(module) : module.installed && module.launch_path ? navigate(moduleDestination(module.launch_path)) : changeInstallation(module)} className={`w-full rounded-xl ${module.installed ? "border border-[#cbd6cf] bg-white text-[#102a27] hover:bg-[#f4f7f3]" : "bg-[#102a27] text-white hover:bg-[#173b36]"}`}>
                        {changing === module.module_id ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : module.installed ? <Check className="mr-2 h-4 w-4" /> : module.status === "coming_soon" ? <Wrench className="mr-2 h-4 w-4" /> : <ExternalLink className="mr-2 h-4 w-4" />}
                        {module.module_id === "wstl" && module.billing?.customer_configured ? "Manage billing" : module.installed && module.launch_path ? "Open module" : module.installed ? "Installed" : module.status === "coming_soon" ? "In development" : module.status === "managed" ? "Manage in Settings" : module.install_label || "Install module"}
                      </Button>
                      {module.installed && module.module_id === "website_pro" && <button type="button" onClick={() => manageBilling(module)} disabled={changing === module.module_id} className="mt-3 w-full rounded-lg py-2 text-center text-xs font-semibold text-[#718078] hover:bg-[#f4f7f3] hover:text-[#102a27] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#087f67]">Manage billing</button>}
                      {module.installed && !["wstl", "website_pro"].includes(module.module_id) && <button type="button" onClick={() => setPendingRemoval(module)} disabled={changing === module.module_id} className="mt-3 w-full rounded-lg py-2 text-center text-xs font-semibold text-[#718078] hover:bg-[#fff5f1] hover:text-[#7a2d20] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#087f67]">{module.remove_label || "Remove module"}</button>}
                    </div>
                  </article>
                ))}
              </div>
            )}
          </div>
        </section>

        <section className="grid gap-5 rounded-[28px] border border-[#dfe6df] bg-white p-6 lg:grid-cols-[1fr_auto] lg:items-center lg:p-8">
          <div>
            <div className="flex items-center gap-2 text-sm font-semibold text-[#087f67]"><LockKeyhole className="h-4 w-4" /> Developer Platform</div>
            <h2 className="mt-2 text-2xl font-semibold text-[#102a27]">Build on Sporthouse safely.</h2>
            <p className="mt-2 max-w-3xl text-sm leading-6 text-[#60706a]">Versioned APIs, scoped OAuth access, signed webhooks, sandboxes, review, and audit controls are the required foundation before third-party submissions open.</p>
          </div>
          <Button onClick={() => navigate(embedded ? `${routeBase}/developer` : "/admin/developer")} className="rounded-xl bg-[#102a27] text-white hover:bg-[#173b36]"><Wrench className="mr-2 h-4 w-4" /> Open developer console</Button>
        </section>
      </div>

      <AlertDialog open={Boolean(pendingRemoval)} onOpenChange={(open) => !open && setPendingRemoval(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Remove {pendingRemoval?.name}?</AlertDialogTitle>
            <AlertDialogDescription>
              This removes the module from your organization. Its features will no longer be available to your staff.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={() => pendingRemoval && changeInstallation(pendingRemoval)} disabled={Boolean(changing)} className="bg-[#a33d2c] text-white hover:bg-[#873224]">
              {changing ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
              Remove module
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </PageLayout>
  );
};

export default AdminMarketplace;
